import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Table,
  Upload,
  InputNumber,
  message,
  Image,
  Spin,
  Card,
  Tooltip,
  Modal,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  UploadOutlined,
  EditOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import useUpload from "../../../../hooks/use-upload";
import { productTypes } from "../../../../types";
import shortid from "shortid";
import { useMutation } from "@apollo/client";
import {
  CREATE_PRODUCT_VARIANT,
  UPDATE_PRODUCT_VARIANT,
  DELETE_PRODUCT_VARIANT,
  CHANGE_PRIMARY_VARIANT,
} from "./requests";

const { Text } = Typography;

interface VariantFormProps {
  product: typeof productTypes.product | null;
  variants: typeof productTypes.productVariant[] | null;
  setVariants: (value: typeof productTypes.productVariant[] | null) => void;
  isCreateView: boolean;
  refetch?: () => void;
}

const VariantForm: React.FC<VariantFormProps> = ({ product, variants, setVariants, isCreateView, refetch }) => {
  const [form] = Form.useForm();
  const { upload } = useUpload();
  const [variantsList, setVariantsList] = useState<typeof productTypes.productVariant[]>(variants || []);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadLoding, setUploadLoading] = useState(false);
  const [loadingPrimary, setLoadingPrimary] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState<string | null>(null);
  const [editingVariant, setEditingVariant] = useState<typeof productTypes.productVariant | null>(null)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const [createProductMutation] = useMutation(CREATE_PRODUCT_VARIANT);
  const [updateProductMutation] = useMutation(UPDATE_PRODUCT_VARIANT);
  const [deleteProductMutation] = useMutation(DELETE_PRODUCT_VARIANT);
  const [primaryVariantMutation] = useMutation(CHANGE_PRIMARY_VARIANT);

  useEffect(() => {
    if(!isCreateView){
      setVariantsList(variants ?? []);
    }
  }, [variants]);

  useEffect(() => {
    const values = form.getFieldsValue(["title", "description", "price"]);
    const isFormComplete = values.title && values.description && values.price;
    setIsButtonDisabled(!isFormComplete || !imageUrl);
  }, [form, imageUrl]);

  useEffect(() => {
    if (editingVariant) {
      form.setFieldsValue({
        title: editingVariant.title || product?.name,
        description: editingVariant.description || product?.description,
        price: editingVariant.price,
        comparePrice: editingVariant.comparePrice,
      });
      setImageUrl(editingVariant.img || null);
    }
  }, [editingVariant]);

  const addVariant = async (values: any) => {
    setLoading(true);
    if (isCreateView) {
      if (editingVariant) {
        const updatedVariants = variantsList.map((variant) =>
          variant.id === editingVariant.id
            ? {
              ...variant,
              title: values.title,
              price: values.price,
              comparePrice: values.comparePrice || null,
              img: imageUrl || undefined,
              description: values.description,
            }
            : variant);
        setVariantsList(updatedVariants);
        setEditingVariant(null);
        setVariants(updatedVariants)
      } else {
        const newVariant: typeof productTypes.productVariant = {
          id: shortid.generate(),
          title: values.title,
          price: values.price,
          comparePrice: values.comparePrice || null,
          img: imageUrl || undefined,
          description: values.description,
          isPrimaryVariant: variantsList.length === 0 ? true : false,
        };
        setVariantsList([...variantsList, newVariant]);
        setVariants([...variantsList, newVariant])
      }
    } else {
      try{
        const payload = {
          variables: {
            productVariant: {
              product: product?.id,
              title: values.title,
              price: values.price,
              comparePrice: values.comparePrice || null,
              img: imageUrl || undefined,
              description: values.description,
              isPrimaryVariant: variantsList.length === 0 ? true : false,
            },
          },
        }
        const { data } = await createProductMutation(payload);
        await refetch?.();
        const newVariant = data?.createProductVariant?.productVariant;
        setVariantsList([
          ...variantsList,
          { title: newVariant.title,
            id: newVariant.id,
            description: newVariant.description,
            price: newVariant.price,
            img: newVariant.img,
          },
        ]);
      } catch(err) {
        console.log(err)
        message.error("Hubo un error")
      }
    }
    setLoading(false);
    form.resetFields();
    setImageUrl(null);
  };

  const handleFileUpload = async ({ fileList }) => {
    if (fileList[0]) {
      const fileName = `products/${new Date().toISOString()}/${
        fileList[0]?.name
      }`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);

      setImageUrl(url);
    }
    setUploadLoading(false);
    message.success("Imagen agregada.");
  };

  const confirmDeleteVariant = (id: string) => {
    setVariantToDelete(id);
    setModalVisible(true);
  };

  const handleDeleteVariant = async () => {
    setLoadingDelete(true);
    if (variantToDelete) {
      if (isCreateView) {
        const updatedVariants = variantsList.filter((variant) => variant.id !== variantToDelete)
        setVariantsList(updatedVariants);
        setVariants(updatedVariants)
      } else {
        await deleteProductMutation({ variables: { id: variantToDelete } })
        await refetch?.();
        const updatedVariants = variantsList.filter((variant) => variant.id !== variantToDelete)
        setVariantsList(updatedVariants);
      }
    }
    setModalVisible(false);
    setVariantToDelete(null);
    setLoadingDelete(false);
  };

  const handleEditVariant = (variant) => {
    setEditingVariant(variant);
  };

  const setPrimaryVariant = async (id: string) => {
    setLoadingPrimary((prev) => ({ ...prev,
      [id]: true }));
    if(isCreateView) {
      const updatedVariants = variantsList.map((variant) =>
        variant.id === id
          ? { ...variant,
            isPrimaryVariant: true }
          : { ...variant,
            isPrimaryVariant: false });
      setVariantsList(updatedVariants);
      setVariants(updatedVariants)
    } else {
      try {
        await primaryVariantMutation({
          variables: {
            id,
            product: product?.id,
          },
        });
        await refetch?.();
        const updatedVariants = variantsList.map((variant) =>
          variant.id === id
            ? { ...variant,
              isPrimaryVariant: true }
            : { ...variant,
              isPrimaryVariant: false });
        setVariantsList(updatedVariants);
      } catch (error) {
        console.error( error);
      }
    }
    setLoadingPrimary((prev) => ({ ...prev,
      [id]: false }));
  };

  const editVariant = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();

      const payload = {
        id: editingVariant?.id,
        productVariant: {
          title: values.title,
          description: values.description,
          price: values.price,
          comparePrice: values.comparePrice || null,
          img: imageUrl,
        },
      };

      await updateProductMutation({ variables: payload });
      await refetch?.();

      const updatedVariants = variantsList.map((variant) =>
        variant.id === editingVariant?.id
          ? {
            ...variant,
            title: values.title,
            price: values.price,
            img: imageUrl || undefined,
            description: values.description,
          }
          : variant);
      setVariantsList(updatedVariants);
      message.success("Variante editada exitosamente");
    } catch (error) {
      console.log(error);
      message.error("Error al editar la variante");
    } finally {
      setLoading(false);
      setEditingVariant(null);
      setImageUrl(null);
      form.resetFields();
    }
  };

  const columns = [
    {
      title: "Imagen",
      dataIndex: "img",
      key: "img",
      render: (img: string) => (img ? <img src={img} alt="variante" style={{ width: 50 }} /> : null),
    },
    { title: "Nombre",
      key: "title",
      render: (row: typeof productTypes.productVariant) => {
        if(row.title){
          return <Text strong>{row.title}</Text>;
        } else {
          return <Text strong>{product?.name}</Text>;
        }
      },
    },
    { title: "Descripción",
      key: "description",
      render: (row: typeof productTypes.productVariant) => {
        if(row.description){
          return <Text strong>{row.description}</Text>;
        } else {
          return <Text strong>{product?.description}</Text>;
        }
      },
    },
    { title: "Precio",
      dataIndex: "price",
      key: "price" },
    { title: "Precio Comparativo",
      dataIndex: "comparePrice",
      key: "comparePrice",
      render: (value: number | null) => value ? `$${value}` : '-' },
    { title: "Principal",
      dataIndex: "isPrimaryVariant",
      key: "isPrimaryVariant",
      render: (value) => (value ? "Sí" : "No") },
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: typeof productTypes.productVariant) => (
        <div>
          <Tooltip title="Editar">
            <Button
              type="primary"
              style={{ margin: 5 }}
              icon={<EditOutlined />}
              onClick={() => handleEditVariant(record)}
            />
          </Tooltip>
          <Tooltip title="Borrar">
            <Button
              type="primary"
              style={{ margin: 5 }}
              icon={<DeleteOutlined />}
              onClick={() => record.id && confirmDeleteVariant(record.id)}
            />
          </Tooltip>
          <Tooltip title="Establecer como principal">
            <Button
              type="default"
              style={{ margin: 5 }}
              loading={loadingPrimary[record.id as string] || false}
              onClick={() => record.id && setPrimaryVariant(record.id)}
              icon={record.isPrimaryVariant ? <StarFilled /> : <StarOutlined />}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Card
      style={{
        boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
        borderRadius: "15px",
      }}
    >
      <Form layout="vertical" form={form} onFinish={addVariant}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="title" label="Nombre" rules={[{ required: true,
              message: "Ingrese un nombre" }]}>
              <Input allowClear placeholder="Nombre de variante" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="description" label="Descripción" rules={[{ required: true,
              message: "Ingrese una descripción" }]}>
              <Input allowClear placeholder="Descripción de variante" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="price" label="Precio" rules={[{ required: true,
              message: "Ingrese un precio" }]}>
              <InputNumber placeholder="Precio de variante" min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="comparePrice" label="Precio Comparativo (opcional)">
              <InputNumber placeholder="Precio original (tachado)" min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Imagen">
              <Upload
                beforeUpload={() => false}
                listType="picture"
                maxCount={1}
                accept="image/*"
                onChange={(info) => {
                  handleFileUpload(info);
                }}
                showUploadList={false}
              >
                <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>Seleccionar Imagen</Button>
                {uploadLoding ? <Spin style={{ margin: 10 }} /> : null}
              </Upload>
              {imageUrl && (
                <Image style={{ marginLeft: 20 }} width={70} src={imageUrl} />
              )}
            </Form.Item>
          </Col>
        </Row>
        <Tooltip title={isButtonDisabled ? "Por favor ingrese todos los datos" : null}>
          <Button
            type="primary"
            onClick={async () => {
              if(editingVariant) {
                editVariant()
              } else {
                form.submit();
              }
            }}
            style={{ marginBottom: 20 }}
            disabled={isButtonDisabled}
            loading={loading}
          >
            {editingVariant ? "Editar Variante" : "Guardar Variante"}
          </Button>
        </Tooltip>
        {editingVariant && (
          <Button
            onClick={() => {
              form.resetFields();
              setEditingVariant(null);
              setImageUrl(null);
            }}
            style={{ marginBottom: 20,
              marginLeft: 15 }}
          >
            Cancelar
          </Button>
        )}
      </Form>
      <Table dataSource={variantsList} columns={columns} rowKey="id" pagination={false} />
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <p>¿Estás seguro de que quieres eliminar esta variante?</p>
        <div style={{ textAlign: 'right' }}>
          <Button
            onClick={() => setModalVisible(false)}
            style={{ marginRight: 8 }}
          >
           Cancelar
          </Button>
          <Button
            type="primary"
            onClick={handleDeleteVariant}
            loading={loadingDelete}
          >
            Eliminar
          </Button>
        </div>
      </Modal>
    </Card>
  );
};

export default VariantForm;
